import type { GradeTask, Run } from "@/app/gradingQueries";
import type { Notebook, Task } from "@/kernel/runner";

/* S6: всё, что показывает экран, выводится из прогона и манифеста. */

export type TaskState = GradeTask["status"];

export const GRID_LOOK: Record<TaskState, { bg: string; bd: string; fg: string; mark: string; style: "solid" | "dashed"; label: string }> = {
  passed: { bg: "var(--oksoft)", bd: "var(--ok)", fg: "var(--ok)", mark: "ok", style: "solid", label: "прошло" },
  failed: { bg: "var(--errsoft)", bd: "var(--err)", fg: "var(--err)", mark: "fail", style: "solid", label: "упало" },
  idle: { bg: "transparent", bd: "var(--bd2)", fg: "var(--mut)", mark: "—", style: "dashed", label: "не запускалось" },
};

const tasksOf = (run: Run) => run.result?.tasks ?? [];

/** Счётчики «прошло / упало / не запускалось» по задачам результата. */
export function summary(run: Run) {
  const t = tasksOf(run);
  return {
    passed: t.filter((x) => x.status === "passed").length,
    failed: t.filter((x) => x.status === "failed").length,
    idle: t.filter((x) => x.status === "idle").length,
    total: run.total,
  };
}

export function statusOf(run: Run, n: number): TaskState {
  return tasksOf(run).find((t) => t.n === n)?.status ?? "idle";
}

export type Failure = { n: number; title: string; kind: string; assertLine: string; cellId: string; traceback: string[] };

export function failures(run: Run, tasks: Task[]): Failure[] {
  return tasksOf(run)
    .filter((t) => t.status === "failed")
    .map((t) => ({ n: t.n, title: tasks.find((x) => x.n === t.n)?.title ?? "", kind: t.error_kind || "Error", assertLine: t.assert_line, cellId: t.cell_id, traceback: t.traceback }));
}

/** Сводка как в макете («Задача 7: KeyError», строка проверки, сообщение),
 *  затем трейсбек раннера построчно. */
export function traceLines(f: Failure): string[] {
  const tb = f.traceback.join("").split("\n");
  while (tb.length && tb[tb.length - 1]!.trim() === "") tb.pop();
  const message = [...tb].reverse().find((l) => l.trim() !== "" && !/^\s/.test(l));
  const head = [`Задача ${f.n}: ${f.kind}`];
  if (f.assertLine) head.push(`  ${f.assertLine}`);
  if (message) head.push(`  ${message}`);
  return tb.length ? [...head, "", ...tb] : head;
}

/** «ячейка 23 · проверки» — номер ячейки в ноутбуке студента. */
export function cellLabel(nb: Notebook | undefined, task: Task | undefined, cellId: string): string {
  if (!nb || !task) return "";
  const i = nb.cells.findIndex((c) => c.id === cellId);
  if (i < 0) return "";
  return `ячейка ${i + 1} · ${cellId === task.checks_cell_id ? "проверки" : "решение"}`;
}

/** Полоска истории: по клетке на задачу. */
export function stripCells(run: Run, total: number): TaskState[] {
  return Array.from({ length: total }, (_, i) => statusOf(run, i + 1));
}

export function scoreTone(run: Pick<Run, "passed" | "failed" | "idle" | "status">, threshold: number): string {
  if (run.status !== "done" && run.status !== "error") return "var(--mut)";
  if (run.status === "done" && run.passed >= threshold && run.idle === 0) return "var(--ok)";
  return run.failed >= run.idle ? "var(--err)" : "var(--mut)";
}

const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export function runMeta(run: Run): string {
  const where = "CPU · на сервере";
  if (run.status === "queued") return `прогон #${run.n} · в очереди · ${where}`;
  if (run.status === "running") return `прогон #${run.n} · идёт · ${where}`;
  const at = run.finished_at ?? run.created_at;
  const dur = run.result ? ` · ${(run.result.duration_ms / 1000).toFixed(1)} s` : "";
  return `прогон #${run.n} · ${hhmm(at)}${dur} · ${where}`;
}

const GEN: Record<number, string> = { 2: "двух", 3: "трёх", 4: "четырёх", 5: "пяти" };

/** Текст под заблокированной кнопкой: допуск упавших = total − threshold. */
export function blockerText(threshold: number, total: number): string {
  const max = Math.max(0, total - threshold);
  const when =
    max === 0
      ? "когда пройдут все проверки и не останется незапущенных"
      : max === 1
        ? "когда останется не больше одной упавшей проверки и ни одной незапущенной"
        : `когда останется не больше ${GEN[max] ?? max} упавших проверок и ни одной незапущенной`;
  return `Отправка откроется, ${when}. Ментор смотрит процедуру, а не гоняет раннер за тебя.`;
}

/** Прогон в очереди дольше минуты — раннер не отвечает (спека §5.5). */
export const STUCK_MS = 60_000;
export const isStuck = (run: Run, now = Date.now()) => run.status === "queued" && now - Date.parse(run.created_at) > STUCK_MS;

export const ENV_KIND: Record<string, string> = {
  timeout: "не уложился во время",
  memory: "не хватило памяти",
  boot: "среда не поднялась",
  internal: "внутренняя ошибка раннера",
};
